'use client'

import React from 'react'
import { X } from 'lucide-react'
import { UndoLeft } from 'solar-icons'
import { useCart } from '@/lib/hooks'

import { Badge, Button, LoadingDots, Thumbnail } from '@/components/ui'
import { KotFulfillModal } from '@/components/modals'

interface KotCardProps extends React.ComponentPropsWithoutRef<'div'> {
  item?: any
  cartId?: string
}

export const KotCardLineItem = ({ item, cartId }: KotCardProps) => {
  const { updateKotCartItem } = useCart()
  const [processingUndo, setProcessingUndo] = React.useState(false)

  const badgeVariant = () => {
    switch (item?.status_fulfilment) {
      case 'partialy_fulfilled':
        return 'warning'
      case 'fulfilled':
        return 'green'
      default:
        return 'secondary'
    }
  }

  const handleUndoQuantity = () => {
    setProcessingUndo(true)
    updateKotCartItem(
      {
        cartId,
        product_id: item.id,
        qty: item?.qty_fulfilled,
        status_fulfilment: 'pending',
      },
      () => setProcessingUndo(false),
    )
  }

  return (
    <div className="flex w-full flex-col items-center gap-2 border-b border-gray-100 px-4 py-4 hover:bg-gray-50 md:flex-row md:px-8">
      <div className="flex w-full items-start gap-2">
        <Thumbnail
          size="sm"
          className="size-10 shrink-0 border border-gray-100/60"
          src={item.image_url}
        />
        <div className="-mt-1 flex flex-col gap-2 md:gap-1">
          <span className="w-full text-base font-medium text-gray-950">
            {item?.product_name}
          </span>
          <div className="flex items-center gap-2">
            <Badge
              size="lg"
              className="h-6 w-auto gap-0 border-gray-200 bg-gray-50 px-2 pr-1 font-mono text-sm"
              variant="outline"
            >
              {item?.qty_fulfilled} / {item?.quantity}
              <X className="relative size-2.5 shrink-0" strokeWidth={2} />
            </Badge>
            <Badge
              variant={badgeVariant()}
              size="md"
              className="h-6 capitalize"
            >
              {item?.status_fulfilment}
            </Badge>
          </div>
        </div>
      </div>

      <div className="flex w-full gap-3 md:w-[unset] md:items-center">
        {item?.qty_fulfilled > 0 && (
          <Button
            className="w-full md:w-[unset]"
            variant="secondary"
            onClick={handleUndoQuantity}
          >
            {processingUndo && <LoadingDots dotClassName="size-1.5" />}
            {!processingUndo && (
              <>
                <span>Undo</span>
                <UndoLeft strokeWidth={1.2} className="size-5 text-gray-950" />
              </>
            )}
          </Button>
        )}

        {item?.qty_fulfilled !== item?.quantity && (
          <KotFulfillModal item={item} cartId={cartId} />
        )}
      </div>
    </div>
  )
}
